<template>
    <div class="refund-pending-termination">
        <div class="block-header">
            <h3 class="block-title">{{ !isLoading ? 'Refund Processed Successfully' : 'Refund Request Submitted' }}</h3>
            <p class="block-description">
                {{ !isLoading ? refundData?.message : 'Your refund request has been successfully submitted and is being processed.' }}
            </p>
        </div>

        <div class="termination-details">
            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Processing Status</h4>
                </div>
                <div class="card-content">
                    <div class="status-info">
                        <div class="status-row">
                            <span class="status-label">Status:</span>
                            <span class="status-value text-blue-500">{{ refundData?.status === 'complete' ? 'Complete' : 'Processing' }}</span>
                        </div>
                        <div class="status-row">
                            <span class="status-label">Refund Amount:</span>
                            <span class="status-value">
                                <span v-if="isLoading" class="dots-loader">
                                    <span class="dot"></span>
                                    <span class="dot"></span>
                                    <span class="dot"></span>
                                </span>
                                <span v-else>{{ refundData?.amount_formatted || `$${amount.toFixed(2)}` }}</span>
                            </span>
                        </div>
                        <div class="status-row">
                            <span class="status-label">Processing Time:</span>
                            <span class="status-value">5-10 business days</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">What Happens Next</h4>
                </div>
                <div class="card-content">
                    <div class="timeline">
                        <div class="timeline-item">
                            <div class="timeline-icon bg-green-100">
                                <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="timeline-content">
                                <h4>Request Received</h4>
                                <p>Your refund request has been received and is being reviewed</p>
                            </div>
                        </div>
                        <div class="timeline-item">
                            <div class="timeline-icon bg-blue-100">
                                <svg class="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
                                </svg>
                            </div>
                            <div class="timeline-content">
                                <h4>Processing</h4>
                                <p>We're processing your refund and will notify you via email</p>
                            </div>
                        </div>
                        <div class="timeline-item">
                            <div class="timeline-icon">
                                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                                </svg>
                            </div>
                            <div class="timeline-content">
                                <h4>Refund Issued</h4>
                                <p>Your refund will be issued to your original payment method</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="info-card">
                <div class="card-header">
                    <svg class="w-6 h-6 mr-3 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <h4 class="card-title">Important Information</h4>
                </div>
                <div class="card-content">
                    <ul class="important-info">
                        <li>Your license has been cancelled and you may lose access to services</li>
                        <li>You'll receive a confirmation email with your refund details</li>
                        <li>Refunds typically appear in your account within 5-10 business days</li>
                        <li>You can reactivate your account at any time by purchasing a new license</li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="action-buttons">
            <button 
                class="btn btn-primary"
                @click="$emit('close')"
            >
                Close
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useLicenseStore } from '~/stores/person/licenses';
import type { License } from '~/types/person/person';

interface Props {
    license?: License;
    amount: number;
    feedback?: {
        reason?: string;
        additionalFeedback?: string;
    };
}

defineProps<Props>();

const licenseStore = useLicenseStore();

// Computed properties from store
const refundData = computed(() => licenseStore.refundData);
const isLoading = computed(() => licenseStore.isLoading);

const emit = defineEmits<{
    refundError: [errorMessage: string];
    close: [];
}>();

// This component just displays the pending state - the refund is already processed by RefundReviewBlock
</script>

<style lang="scss" scoped>

.refund-pending-termination {
    @apply  sm:p-6;
}

.block-header {
    @apply mb-6 text-center;
    
    .block-title {
        @apply text-2xl font-semibold text-gray-900 mb-2;
    }
    
    .block-description {
        @apply text-gray-600 text-base;
        margin: 0 auto;
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: normal;
    }
}

.termination-details {
    @apply mb-6 space-y-4;
}

.info-card {
    @apply bg-white rounded-lg border border-gray-200 p-4 sm:p-6;
    
    .card-header {
        @apply flex items-center mb-4;
        
        .card-title {
            @apply text-lg font-semibold text-gray-900;
        }
    }
    
    .card-content {
        .status-info {
            .status-row {
                @apply flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0;
                
                .status-label {
                    @apply font-medium text-gray-700;
                }
                
                .status-value {
                    @apply text-gray-900;
                    text-align: right;
                }
            }
        }
        
        .timeline {
            @apply space-y-4;
            
            .timeline-item {
                @apply flex items-start;
                
                .timeline-icon {
                    @apply flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-4 mt-1;
                }
                
                .timeline-content {
                    @apply flex-1;
                    
                    h4 {
                        @apply font-medium text-gray-900 mb-1;
                    }
                    
                    p {
                        @apply text-sm text-gray-600;
                    }
                }
            }
        }
        
        .important-info {
            @apply list-disc list-inside space-y-2 text-gray-700;
        }
    }
}

.dots-loader {
    @apply inline-flex items-center space-x-1;
    
    .dot {
        @apply w-1.5 h-1.5 bg-blue-500 rounded-full;
        animation: dot-bounce 1.4s ease-in-out infinite both;
        
        &:nth-child(1) {
            animation-delay: -0.32s;
        }
        
        &:nth-child(2) {
            animation-delay: -0.16s;
        }
    }
}

@keyframes dot-bounce {
    0%, 80%, 100% {
        transform: scale(0);
        opacity: 0.5;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.action-buttons {
    @apply flex justify-center;
    
    .btn {
        @apply px-6 py-2 rounded-lg font-medium transition-all duration-200;
        
        &.btn-primary {
            @apply bg-blue-500 text-ink-invert border border-blue-500;
            
            &:hover:not(:disabled) {
                @apply bg-blue-600 border-blue-600;
            }
            
            &:disabled {
                @apply opacity-50 cursor-not-allowed;
            }
        }
    }
}
</style> 